/**
 * Where a cover film starts, and how long a cover runs.
 *
 * A cover is a teaser, so it may run for a limited window and no longer. The
 * studio used to *refuse* a longer film: a creator who shot a two-minute
 * introduction was told to go and cut it somewhere else and come back, which is
 * the kind of refusal a person cannot act on with the tool in front of them. A
 * long film is not a mistake — it is a film whose first minute is not the minute
 * they want.
 *
 * So the window is stored instead: `startMs` on the cover record, read here into
 * a `[start, end]` pair. `end` is derived rather than stored because it is fully
 * determined — the window's length is a property of the *surface* the cover plays
 * on — and two numbers that must agree is one more thing that can stop agreeing.
 * The film itself is untouched: no re-encode, no upload of a second copy, and a
 * film already on the server keeps working when the window is ever changed.
 *
 * The window's length is passed in rather than assumed, because the two surfaces
 * want different ones and both are honest: a FlashBook cover is a sixty-second
 * teaser for a book of pages, and a Capsule cover is the intro to a quiz that may
 * run for three minutes. Everything else about the rule — when a trimmer is even
 * offered, how far a thumb travels, what a drag means — is the same arithmetic,
 * so it lives here once with the length as its one parameter.
 *
 * Everything here is pure, including the scrubber's arithmetic, so the rules a
 * creator feels ("how far can I drag, and what does 40% of the track mean") are
 * pinned by tests instead of by eye.
 */

/** A FlashBook cover: the one place both the copy and the check read its length. */
export const COVER_VIDEO_MAX_SECONDS = 60
export const COVER_VIDEO_MAX_MS = COVER_VIDEO_MAX_SECONDS * 1000

/**
 * A Capsule cover: three minutes, which is what its own picker copy promises.
 *
 * It used to be the picker's `videoMaxDuration` of five, so the studio said "max
 * 3 min" while accepting five and then refusing anything longer — the mismatch a
 * creator met as a film that simply would not import.
 */
export const CAPSULE_COVER_MAX_SECONDS = 180
export const CAPSULE_COVER_MAX_MS = CAPSULE_COVER_MAX_SECONDS * 1000

/**
 * Whether a film is longer than a cover may run, and so has a window to choose.
 *
 * A missing duration is not an over-length one: the platform did not measure it,
 * and sending a creator into a trimmer whose ruler has no numbers on it would be
 * a worse experience than playing their film whole.
 */
export function coverNeedsTrim(
  durationMs?: number | null,
  windowMs: number = COVER_VIDEO_MAX_MS,
): boolean {
  if (typeof durationMs !== 'number' || durationMs <= 0) return false
  return durationMs > windowMs
}

/**
 * The largest start that still leaves a full window inside the film.
 *
 * Zero for anything that already fits: a 40-second film has nowhere to be
 * trimmed to, and a scrubber that could slide it would only expose blank film.
 */
export function coverMaxStartMs(
  durationMs?: number | null,
  windowMs: number = COVER_VIDEO_MAX_MS,
): number {
  if (typeof durationMs !== 'number' || durationMs <= 0) return 0
  return Math.max(0, durationMs - windowMs)
}

/**
 * The window a cover plays.
 *
 * One reading of a start and a length, used by the player, the studio preview and
 * the trimmer alike, so all three agree on what the creator will hear and see. An
 * unknown length yields an open-ended window at zero, which is how the player
 * behaves for a film whose duration only the element knows.
 */
export function coverWindow(
  durationMs?: number | null,
  startMs?: number | null,
  windowMs: number = COVER_VIDEO_MAX_MS,
): {startMs: number; endMs: number} {
  const requested = Math.max(
    0,
    Math.round(typeof startMs === 'number' ? startMs : 0),
  )
  /*
   * A film whose length is not known *yet*.
   *
   * This is the normal case on the first frame of playback: a record says the
   * cover opens at 0:30 and the element has not measured the film, so there is
   * nothing to clamp against. Honouring the start is what keeps the window from
   * being dropped and re-applied — a cover that opened at the beginning for a
   * frame and then jumped would be worse than one that never opened there. Only a
   * *trimmer* needs a length, and a trimmer cannot be opened without one.
   */
  if (typeof durationMs !== 'number' || durationMs <= 0) {
    return {startMs: requested, endMs: requested + windowMs}
  }
  const start = Math.min(requested, coverMaxStartMs(durationMs, windowMs))
  const length = Math.min(windowMs, durationMs)
  return {startMs: start, endMs: start + length}
}

/**
 * What a point on the scrubber means: a fraction of the track, as a start.
 *
 * The track is the *travel* rather than the film, because that is what a creator
 * is looking at: the left end is the earliest minute they may open on and the
 * right end the latest, and the thumb cannot be dragged past either. A film with
 * no travel at all (it already fits) always answers zero.
 */
export function coverStartFromFraction(
  fraction: number,
  durationMs?: number | null,
  windowMs: number = COVER_VIDEO_MAX_MS,
): number {
  const travel = coverMaxStartMs(durationMs, windowMs)
  if (travel <= 0) return 0
  const clamped = Math.min(Math.max(fraction, 0), 1)
  return Math.round(clamped * travel)
}

/** The inverse, for drawing the thumb: where a start sits on the track. */
export function coverFractionFromStart(
  startMs: number,
  durationMs?: number | null,
  windowMs: number = COVER_VIDEO_MAX_MS,
): number {
  const travel = coverMaxStartMs(durationMs, windowMs)
  if (travel <= 0) return 0
  return Math.min(Math.max(startMs / travel, 0), 1)
}
